const integerFormatter = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 0,
});

export function formatIntegerQuantity(value: string | null | undefined) {
  if (value == null) return "--";
  return integerFormatter.format(BigInt(value));
}

export function formatBasisPoints(value: number | null | undefined) {
  if (value == null) return "--";
  const whole = Math.trunc(value / 100);
  const decimal = value % 100;
  return decimal === 0
    ? `${whole}%`
    : `${whole}.${String(decimal).padStart(2, "0").replace(/0+$/, "")}%`;
}

export function formatBytes(value: string | null | undefined) {
  if (value == null) return "--";
  const bytes = BigInt(value);
  const absolute = bytes < 0n ? -bytes : bytes;
  const units = [
    { label: "TB", size: 1024n ** 4n },
    { label: "GB", size: 1024n ** 3n },
    { label: "MB", size: 1024n ** 2n },
    { label: "KB", size: 1024n },
  ];
  const unit = units.find((candidate) => absolute >= candidate.size);
  if (!unit) return `${integerFormatter.format(bytes)} B`;
  const roundedAbsoluteTenths = (absolute * 10n + unit.size / 2n) / unit.size;
  const scaledTenths =
    bytes < 0n ? -roundedAbsoluteTenths : roundedAbsoluteTenths;
  const whole = scaledTenths / 10n;
  const decimal =
    scaledTenths < 0n ? -(scaledTenths % 10n) : scaledTenths % 10n;
  return decimal === 0n
    ? `${integerFormatter.format(whole)} ${unit.label}`
    : `${integerFormatter.format(whole)}.${decimal} ${unit.label}`;
}

export function formatMinorCurrency(
  amountMinor: string | null | undefined,
  currencyCode: string | null | undefined,
) {
  if (amountMinor == null || !currencyCode) return "--";
  const amount = BigInt(amountMinor);
  const absolute = amount < 0n ? -amount : amount;
  const fractionDigits = currencyFractionDigits(currencyCode);
  const scale = 10n ** BigInt(fractionDigits);
  const major = absolute / scale;
  const minor = absolute % scale;
  const sign = amount < 0n ? "-" : "";
  const symbol = currencySymbol(currencyCode);
  const fraction =
    fractionDigits === 0
      ? ""
      : `.${String(minor).padStart(fractionDigits, "0")}`;
  return `${sign}${symbol}${integerFormatter.format(major)}${fraction}`;
}

// 只转换用户输入的货币单位，不计算价格、汇率或积分换算。
export function parseCurrencyAmountToMinor(
  value: string,
  currencyCode: string,
) {
  if (
    !currencyCode ||
    value.length > 32 ||
    !/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)
  )
    return null;
  const digits = currencyFractionDigits(currencyCode);
  const [whole = "0", fraction = ""] = value.split(".");
  if (fraction.length > digits) return null;
  const amount =
    BigInt(whole) * 10n ** BigInt(digits) +
    BigInt(fraction.padEnd(digits, "0") || "0");
  return amount > 0n && amount <= 9223372036854775807n
    ? amount.toString()
    : null;
}

export function formatBillingDate(value: string | null | undefined) {
  if (!value) return "--";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    year: "numeric",
  }).format(new Date(value));
}

export function formatShortDate(value: string | null | undefined) {
  if (!value) return "--";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
  }).format(new Date(value));
}

export function formatPeriod(unit: string | null, count: number | null) {
  if (!unit || !count) return "";
  const label = { day: "天", month: "月", one_time: "次", year: "年" }[unit];
  return label ? `${count}${label}` : "";
}

function currencySymbol(currencyCode: string) {
  try {
    const currency = new Intl.NumberFormat("zh-CN", {
      currency: currencyCode,
      currencyDisplay: "symbol",
      style: "currency",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value;
    if (!currency) return `${currencyCode} `;
    return /^[A-Z]{3}$/u.test(currency) || currencyCode === "USD"
      ? `${currency} `
      : currency;
  } catch {
    return `${currencyCode} `;
  }
}

function currencyFractionDigits(currencyCode: string) {
  try {
    return (
      new Intl.NumberFormat("zh-CN", {
        currency: currencyCode,
        style: "currency",
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}
